import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  RefreshCw, Inbox, Phone, Mail, Calendar, ArrowLeft, Loader2, Lock, LogOut, Heart,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "bb_admin_token";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

function LoginGate({ onSuccess }) {
  const [passcode, setPasscode] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!passcode) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { passcode });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      onSuccess(res.data.token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Incorrect passcode");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFAF5] flex items-center justify-center px-5">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm bg-white rounded-3xl p-8 border border-slate-100 shadow-sm"
      >
        <div className="flex flex-col items-center text-center">
          <span className="flex items-center justify-center w-14 h-14 rounded-2xl bg-sky-400 text-white shadow-[0_4px_14px_0_rgba(56,189,248,0.39)]">
            <Lock className="w-6 h-6" />
          </span>
          <h1 className="mt-4 text-2xl font-extrabold text-slate-900">Admin Access</h1>
          <p className="mt-1 text-sm text-slate-500">Enter the passcode to view tour requests.</p>
        </div>
        <input
          data-testid="admin-passcode-input"
          type="password"
          value={passcode}
          onChange={(e) => setPasscode(e.target.value)}
          placeholder="Passcode"
          className="mt-6 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-sky-400 transition"
        />
        <button
          data-testid="admin-login-button"
          type="submit"
          disabled={loading}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-sky-400 hover:bg-sky-500 text-white font-bold py-3 rounded-full shadow-[0_4px_14px_0_rgba(56,189,248,0.39)] hover:-translate-y-0.5 transition-all disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Unlock"}
        </button>
        <a href="/" className="mt-4 block text-center text-sm font-semibold text-sky-500 hover:underline">
          Back to site
        </a>
      </form>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  const load = async (t) => {
    const authToken = t || token;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tour-requests`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setRequests(res.data);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        logout();
      } else {
        setRequests([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!token) {
    return (
      <LoginGate
        onSuccess={(t) => {
          setToken(t);
          load(t);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFAF5] px-5 md:px-10 py-10">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <a
              data-testid="admin-back-link"
              href="/"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-sky-500 hover:underline"
            >
              <ArrowLeft className="w-4 h-4" /> Back to site
            </a>
            <h1 className="mt-2 text-3xl font-extrabold text-slate-900">Tour Requests</h1>
            <p className="text-slate-500">
              {loading ? "Loading…" : `${requests.length} request${requests.length === 1 ? "" : "s"} received`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={() => load()}
              className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-800 font-bold py-2.5 px-5 rounded-full transition"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold py-2.5 px-5 rounded-full transition"
            >
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
        </div>

        <div className="mt-8" data-testid="admin-requests-list">
          {loading ? (
            <div className="flex justify-center py-20 text-sky-500">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
          ) : requests.length === 0 ? (
            <div
              data-testid="admin-empty-state"
              className="flex flex-col items-center text-center py-20 text-slate-400"
            >
              <Inbox className="w-14 h-14" />
              <p className="mt-4 text-lg font-semibold text-slate-600">No tour requests yet</p>
              <p className="text-sm">New submissions from the website will appear here.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {requests.map((r) => (
                <div
                  key={r.id}
                  data-testid={`admin-request-card-${r.id}`}
                  className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-slate-900">{r.parent_name}</h3>
                    <span className="text-xs font-semibold text-sky-600 bg-sky-50 px-3 py-1 rounded-full">
                      Age {r.child_age}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-slate-400">{fmtDate(r.created_at)}</p>

                  <div className="mt-4 space-y-2 text-sm">
                    <a href={`mailto:${r.email}`} className="flex items-center gap-2 text-slate-700 hover:text-sky-500">
                      <Mail className="w-4 h-4 text-sky-400 shrink-0" /> {r.email}
                    </a>
                    <a
                      href={`tel:${(r.phone || "").replace(/[^0-9]/g, "")}`}
                      className="flex items-center gap-2 text-slate-700 hover:text-sky-500"
                    >
                      <Phone className="w-4 h-4 text-rose-400 shrink-0" /> {r.phone}
                    </a>
                    {r.preferred_date && (
                      <p className="flex items-center gap-2 text-slate-700">
                        <Calendar className="w-4 h-4 text-amber-400 shrink-0" /> Prefers {r.preferred_date}
                      </p>
                    )}
                  </div>

                  {r.message && (
                    <p className="mt-4 text-sm text-slate-600 bg-[#FDFAF5] rounded-xl p-3 border border-slate-100">
                      "{r.message}"
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
